import React, { useEffect, useState, useRef } from 'react';
/**
 * CategorizeRenderer — Assign items to categories by tapping.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
export function CategorizeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const categories: string[] = config.categories || [];
  const items: Array<{
    text: string;
    correctCategory: number;
  }> = config.items || [];
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setAssignments({});
    setSelectedItem(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(Object.keys(assignments).length === items.length && items.length > 0);
  }, [assignments, items.length, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(assignments));
      setSelectedItem(null);
    }
  }, [shouldValidate, assignments, onAnswer]);
  const handleItemClick = (index: number) => {
    if (shouldValidate) return;
    if (assignments[index] !== undefined) {
      const next = {
        ...assignments
      };
      delete next[index];
      setAssignments(next);
      setSelectedItem(index);
    } else {
      setSelectedItem(selectedItem === index ? null : index);
    }
  };
  const handleCategoryClick = (categoryIndex: number) => {
    if (shouldValidate || selectedItem === null) return;
    setAssignments(prev => ({
      ...prev,
      [selectedItem]: categoryIndex
    }));
    setSelectedItem(null);
  };
  if (categories.length === 0 || items.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7927">
        <p className="text-red-700 font-bold" data-id="element-7928">
          ⚠️ Configuratie ontbreekt (categories/items)
        </p>
      </div>;
  }
  const unassignedItems = items.map((item, index) => ({
    ...item,
    index
  })).filter(item => assignments[item.index] === undefined);
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-8" data-id="element-7929">
      {/* Unassigned Items Pool */}
      <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 shadow-inner min-h-[120px]" data-id="element-7930">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center" data-id="element-7931">
          {unassignedItems.length > 0 ? 'Kies een woord en tik op een vak' : 'Alles is gesorteerd!'}
        </h3>
        <div className="flex flex-wrap justify-center gap-3" data-id="element-7932">
          <AnimatePresence mode="popLayout" data-id="element-7933">
            {unassignedItems.map(item => {
            const isSelected = selectedItem === item.index;
            return <motion.button layout layoutId={`item-${item.index}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} key={`item-${item.index}`} onClick={() => handleItemClick(item.index)} disabled={shouldValidate} className={`px-5 py-3 rounded-xl font-bold text-lg transition-all shadow-sm ${shouldValidate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1 hover:shadow-md'} ${isSelected ? 'text-white scale-105' : 'bg-white text-slate-700 border-2 border-slate-200'}`} style={isSelected ? {
              backgroundColor: color,
              boxShadow: `0 4px 14px ${hexToRgba(color, 0.2)}`,
              outlineColor: hexToRgba(color, 0.3),
              '--tw-ring-color': hexToRgba(color, 0.3),
              '--tw-ring-offset-width': '0px',
              '--tw-ring-offset-color': '#fff',
              '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
              '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
              boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 4px 14px rgba(0,0,0,0.1)'
            } as TailwindCSSProperties : undefined} onMouseEnter={e => {
              if (!isSelected && !shouldValidate) {
                e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
              }
            }} onMouseLeave={e => {
              if (!isSelected && !shouldValidate) {
                e.currentTarget.style.borderColor = '';
              }
            }} aria-pressed={isSelected} data-id="element-7934">
                  {item.text}
                </motion.button>;
          })}
          </AnimatePresence>
          {unassignedItems.length === 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center justify-center text-emerald-500 font-bold gap-2 py-2" data-id="element-7935">
              <CheckCircle2 className="w-6 h-6" data-id="element-7936" />
              <span data-id="element-7937">Klaar om te controleren</span>
            </motion.div>}
        </div>
      </div>

      {/* Categories Grid */}
      <div className={`grid gap-4 ${categories.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-7938">
        {categories.map((category, catIndex) => {
        const categoryItems = items.map((item, index) => ({
          ...item,
          index
        })).filter(item => assignments[item.index] === catIndex);
        const isTargetable = selectedItem !== null && !shouldValidate;
        return <div key={`cat-${catIndex}`} onClick={() => handleCategoryClick(catIndex)} className={`flex flex-col rounded-2xl border-2 transition-all overflow-hidden min-h-[200px] ${isTargetable ? 'cursor-pointer' : 'border-slate-200 bg-white'} ${shouldValidate ? 'opacity-90' : ''}`} style={isTargetable ? {
          borderColor: hexToRgba(color, 0.4),
          backgroundColor: hexToRgba(color, 0.05),
          '--tw-ring-color': hexToRgba(color, 0.15),
          '--tw-ring-offset-width': '0px',
          '--tw-ring-offset-color': '#fff',
          '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
          '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
          boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000'
        } as TailwindCSSProperties : undefined} role="region" aria-label={`Categorie: ${category}`} data-id="element-7939">
              <div className={`p-4 text-center border-b-2 font-bold text-lg ${isTargetable ? '' : 'bg-slate-100 border-slate-200 text-slate-700'}`} style={isTargetable ? {
            backgroundColor: hexToRgba(color, 0.1),
            borderColor: hexToRgba(color, 0.2),
            color: color
          } : undefined} data-id="element-7940">
                {category}
                <div className="text-xs font-medium opacity-60 mt-1" data-id="element-7941">
                  {categoryItems.length}{' '}
                  {categoryItems.length === 1 ? 'woord' : 'woorden'}
                </div>
              </div>
              <div className={`flex-1 p-4 flex flex-col gap-2`} style={isTargetable ? {
            backgroundColor: hexToRgba(color, 0.03)
          } : undefined} data-id="element-7942">
                <AnimatePresence mode="popLayout" data-id="element-7943">
                  {categoryItems.map(item => {
                let validationClass = 'bg-white border-slate-200 text-slate-700';
                let ValidationIcon = null;
                if (shouldValidate) {
                  const isCorrect = item.correctCategory === catIndex;
                  validationClass = isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800';
                  ValidationIcon = isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-7944" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" data-id="element-7945" />;
                }
                return <motion.button layout layoutId={`item-${item.index}`} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} key={`item-${item.index}`} onClick={e => {
                  e.stopPropagation();
                  handleItemClick(item.index);
                }} disabled={shouldValidate} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium border-2 shadow-sm ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow'} ${validationClass}`} data-id="element-7946">
                        <span className="truncate" data-id="element-7947">{item.text}</span>
                        {ValidationIcon}
                      </motion.button>;
              })}
                </AnimatePresence>
                {categoryItems.length === 0 && <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-medium border-2 border-dashed border-slate-200 rounded-xl m-2 bg-slate-50/50" data-id="element-7948">
                    {isTargetable ? 'Tik hier om te plaatsen' : 'Leeg'}
                  </div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}